<template>
  <v-dialog v-model="show" max-width="1000px" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span class="mr-5">Institution auswählen</span>
        <v-btn outlined small color="green" @click="showCreateNew = true">
          <v-icon left>mdi-plus</v-icon>
          Neue Institution anlegen
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row>
            <v-col class="d-flex align-center mt-3">
              <v-select 
                hide-details 
                v-model="localEditedItem.fk_institution_id" 
                :items="institutions" 
                item-text="name" 
                item-value="id" 
                label="Institution" 
                dense 
                outlined 
                clearable 
                clear-icon="mdi-close-circle" 
                prepend-inner-icon="mdi-home-search" 
                @change="onInstitutionChange"
              />
              <v-btn 
                class="ml-5" 
                v-if="localEditedItem && localEditedItem.fk_institution_id" 
                outlined 
                @click="clearInstitutionSelection"
              >
                Auswahl entfernen
              </v-btn>
            </v-col>
          </v-row>
          
          <!-- Selected institution details -->
          <v-card outlined v-if="localEditedItem && localEditedItem.fk_institution_id && selectedInstitution" class="mt-5">
            <v-card-title>
              <span class="mr-3">Ausgewählte Institution</span>
              <v-btn outlined small @click="editInstitution" class="mr-5">
                <v-icon left>mdi-pencil</v-icon>
                Bearbeiten
              </v-btn>
              <v-btn outlined small @click="deleteInstitution" :color="$store.state.theme.red">
                <v-icon left>mdi-delete</v-icon>
                Löschen
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col class="my-0 pt-0 pb-3">
                  <!-- Checkbox state managed locally via localEditedItem to ensure cancel truly discards changes -->
                  <v-checkbox 
                    :input-value="localEditedItem && localEditedItem.rechnungs_empfänger === 'institution'" 
                    @change="updateLocalRecipient"
                    label="Als Rechnungsempfänger festlegen" 
                    dense
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="my-0 py-0">
                  <v-text-field 
                    :value="selectedInstitution.name" 
                    dense 
                    outlined 
                    label="Name" 
                    readonly 
                  />
                </v-col>
                <v-col cols="12" class="my-0 py-0">
                  <v-textarea 
                    :value="selectedInstitution.anschrift" 
                    dense 
                    outlined 
                    label="Anschrift" 
                    readonly 
                  />
                </v-col>
                <v-col class="my-0 py-0" cols="12" sm="6">
                  <v-text-field 
                    :value="selectedInstitution.email" 
                    dense 
                    outlined 
                    label="E-Mail" 
                    readonly 
                  />
                </v-col>
                <v-col class="my-0 py-0" cols="12" sm="6">
                  <v-text-field 
                    :value="selectedInstitution.telefon" 
                    dense 
                    outlined 
                    label="Telefon" 
                    readonly 
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          
          <!-- Create/Edit institution dialog -->
          <v-dialog v-model="showCreateNew" max-width="1000px" persistent>
            <v-card outlined>
              <v-card-title class="mb-3">
                <span class="mr-3">
                  {{ editedInstitution.id ? 'Institution bearbeiten' : 'Neue Institution' }}
                </span>
              </v-card-title>
              <v-card-text>
                <v-form v-model="validInstitution" ref="institutionForm">
                  <v-row>
                    <v-col cols="12" class="my-0 py-0">
                      <v-text-field 
                        v-model="editedInstitution.name" 
                        dense 
                        outlined 
                        label="Name" 
                        :rules="nameRules"
                      />
                    </v-col>
                    <v-col cols="12" class="my-0 py-0">
                      <v-textarea 
                        v-model="editedInstitution.anschrift" 
                        dense 
                        outlined 
                        label="Anschrift" 
                        :rules="addressRules"
                      />
                    </v-col>
                    <v-col class="my-0 py-0" cols="12" sm="6">
                      <v-text-field 
                        v-model="editedInstitution.email" 
                        dense 
                        outlined 
                        label="E-Mail" 
                      />
                    </v-col>
                    <v-col class="my-0 py-0" cols="12" sm="6">
                      <v-text-field 
                        v-model="editedInstitution.telefon" 
                        dense 
                        outlined 
                        label="Telefon" 
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-card-actions class="px-6 pb-5">
                <v-spacer></v-spacer>
                <v-btn 
                  :color="$store.state.theme.primary" 
                  @click="closeInstitutionForm" 
                  text
                >
                  Abbrechen
                </v-btn>
                <v-btn 
                  :color="$store.state.theme.green" 
                  @click="saveInstitution" 
                  outlined
                >
                  Speichern
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
      </v-card-text>
      <v-card-actions class="d-flex justify-end px-6 pb-5 pt-0 mt-0">
        <v-btn 
          :color="$store.state.theme.primary" 
          text 
          @click="close" 
          :disabled="saving_institution"
        >
          Abbrechen
        </v-btn>
        <v-btn 
          :color="$store.state.theme.green" 
          outlined 
          @click="save" 
          :loading="saving_institution"
        >
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>
    
    <!-- Delete institution confirmation dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>Institution löschen</v-card-title>
        <v-card-text>
          <p>Diese Institution wird bei allen zugeordneten Klienten entfernt.</p>
          <p class="font-weight-bold">Möchtest du wirklich fortfahren?</p>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn 
            :color="$store.state.theme.primary" 
            :disabled="deleting" 
            text 
            @click="showDeleteDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn 
            :color="$store.state.theme.red" 
            :loading="deleting" 
            text 
            @click="deleteInstitutionConfirm"
          >
            Institution bei allen Klienten löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import connector from '@/helpers/supabase-connector.js';

export default {
  name: 'InstitutionEditDialog',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    editedItem: {
      type: Object,
      required: true
    },
    institutions: {
      type: Array,
      required: true
    },
    session: {
      type: Object,
      required: true
    },
    saving_institution: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      validInstitution: true,
      showCreateNew: false,
      showDeleteDialog: false,
      deleting: false,
      
      // Local copy of editedItem - all changes are made here until save
      // This ensures cancel truly discards changes (no parent state modification)
      // Initialize with empty object to prevent null reference errors
      localEditedItem: {
        fk_institution_id: null,
        rechnungs_empfänger: null
      },
      
      // For creating/editing institutions
      editedInstitution: {
        id: null,
        uid: null,
        name: null,
        anschrift: null,
        email: null,
        telefon: null
      },
      
      defaultInstitution: {
        id: null,
        uid: null,
        name: null,
        anschrift: null,
        email: null,
        telefon: null
      },
      
      // Validation rules
      nameRules: [
        v => !!v || 'Bitte gib einen Namen für die Institution an.'
      ],
      addressRules: [
        v => !!v || 'Bitte gib eine Anschrift für die Institution an.'
      ]
    };
  },
  
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    
    selectedInstitution() {
      if (!this.localEditedItem || !this.localEditedItem.fk_institution_id) {
        return null;
      }
      return this.institutions.find(i => i.id === this.localEditedItem.fk_institution_id) || null;
    }
  },
  
  watch: {
    // Initialize local state when dialog opens
    value: {
      handler(newVal, oldVal) {
        // Dialog is closing - reset to empty state
        if (oldVal && !newVal) {
          this.localEditedItem = {
            fk_institution_id: null,
            rechnungs_empfänger: null
          };
          this.resetInstitutionForm();
        }
        // Dialog is opening - initialize local copy from parent's editedItem
        else if (!oldVal && newVal && this.editedItem) {
          this.localEditedItem = JSON.parse(JSON.stringify(this.editedItem));
        }
      }
    },
    
    // Update local copy when parent's editedItem changes while dialog is open
    editedItem: {
      handler(newVal) {
        // Only sync with parent when dialog is open
        if (newVal && this.show) {
          // If this is the initial dialog open, copy everything
          if (!this.localEditedItem.fk_institution_id && newVal.fk_institution_id) {
            this.localEditedItem = JSON.parse(JSON.stringify(newVal));
          }
          // Otherwise only sync parent-controlled fields
          else {
            // Only update fields that parent explicitly manages
            // This preserves all selections in the form
            this.localEditedItem.rechnungs_empfänger = newVal.rechnungs_empfänger;
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    // Handle checkbox changes locally - only one recipient can be selected
    updateLocalRecipient(isChecked) {
      if (!this.localEditedItem) return;
      
      if (isChecked) {
        this.localEditedItem.rechnungs_empfänger = 'institution';
      } else {
        // Only set to null if institution was actually the recipient
        // Otherwise keep the existing value (could be 'vater', 'mutter', 'partner')
        if (this.localEditedItem.rechnungs_empfänger === 'institution') {
          this.localEditedItem.rechnungs_empfänger = null;
        }
      }
      
    },
    
    onInstitutionChange() {
      // Institution selection changed
      // No validation needed - removed redundant validation code
    },
    
    clearInstitutionSelection() {
      if (!this.localEditedItem) return;
      
      this.localEditedItem.fk_institution_id = null;
      // If institution was the recipient, clear that too
      if (this.localEditedItem.rechnungs_empfänger === 'institution') {
        this.localEditedItem.rechnungs_empfänger = null;
      }
    },
    
    editInstitution() {
      if (this.selectedInstitution) {
        this.editedInstitution = Object.assign({}, this.selectedInstitution);
        this.showCreateNew = true;
      }
    },
    
    deleteInstitution() {
      this.showDeleteDialog = true;
    },
    
    async deleteInstitutionConfirm() {
      this.deleting = true;
      try {
        const deleted = await connector.deleteInstitution(
          this, 
          this.session.user.id, 
          this.selectedInstitution.id
        );
        
        if (deleted) {
          // Clear local state
          if (this.localEditedItem) {
            this.localEditedItem.fk_institution_id = null;
            if (this.localEditedItem.rechnungs_empfänger === 'institution') {
              this.localEditedItem.rechnungs_empfänger = null;
            }
          }
          
          this.$emit('showInfo', { 
            message: 'Institution erfolgreich gelöscht.', 
            timeout: 5000 
          });
          
          // Emit event to refresh institutions list
          this.$emit('institutionDeleted');
          
          this.showDeleteDialog = false;
        }
      } finally {
        this.deleting = false;
      }
    },
    
    resetInstitutionForm() {
      this.editedInstitution = Object.assign({}, this.defaultInstitution);
      this.editedInstitution.uid = this.session.user.id;
      this.showCreateNew = false;
      if (this.$refs.institutionForm) {
        this.$refs.institutionForm.resetValidation();
      }
    },
    
    closeInstitutionForm() {
      this.resetInstitutionForm();
    },
    
    async saveInstitution() {
      if (!this.$refs.institutionForm.validate()) {
        return;
      }
      
      try {
        if (this.editedInstitution.id) {
          // Update existing institution
          const updated = await connector.update(
            this, 
            'institutionen', 
            {
              name: this.editedInstitution.name,
              anschrift: this.editedInstitution.anschrift,
              email: this.editedInstitution.email,
              telefon: this.editedInstitution.telefon
            }, 
            this.editedInstitution.id
          );
          
          if (updated && updated.length === 1) {
            this.$emit('showInfo', { 
              message: 'Institution erfolgreich aktualisiert.', 
              timeout: 5000 
            });
            this.$emit('institutionUpdated');
            this.closeInstitutionForm();
          }
        } else {
          // Insert new institution
          const id = await connector.insert(
            this, 
            'institutionen', 
            {
              uid: this.session.user.id,
              name: this.editedInstitution.name,
              anschrift: this.editedInstitution.anschrift,
              email: this.editedInstitution.email,
              telefon: this.editedInstitution.telefon
            }
          );
          
          if (id) {
            // Select the newly created institution
            if (this.localEditedItem) {
              this.localEditedItem.fk_institution_id = id;
            }
            
            this.$emit('showInfo', { 
              message: 'Institution erfolgreich hinzugefügt und ausgewählt.', 
              timeout: 5000 
            });
            this.$emit('institutionCreated');
            this.closeInstitutionForm();
          }
        }
      } catch (error) {
        this.$emit('showError', { 
          message: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.', 
          timeout: 5000 
        });
      }
    },
    
    close() {
      // Update the v-model to trigger the show setter
      this.show = false;
      // Emit the close event for parent to handle any additional logic
      this.$emit('close', true);
    },
    
    save() {
      if (!this.localEditedItem) {
        return;
      }
      
      // Build update object with only the fields this dialog is responsible for
      const finalItemToEmit = {};
      
      // Include institution selection
      finalItemToEmit.fk_institution_id = this.localEditedItem.fk_institution_id || null;
      
      // Only include rechnungs_empfänger if it changed or if institution is involved
      // This prevents overwriting other selections (vater, mutter, partner)
      if (this.localEditedItem.rechnungs_empfänger === 'institution' || 
          this.editedItem.rechnungs_empfänger === 'institution') {
        finalItemToEmit.rechnungs_empfänger = this.localEditedItem.rechnungs_empfänger;
      }
      
      this.$emit('save', finalItemToEmit);
    }
  }
};
</script>

<style scoped>
/* Add any component-specific styles here */
</style>